import React, {
    useState, useEffect, useContext, useRef, useCallback
} from 'react';
import { TrackingContext } from '@vrbo/react-event-tracking';
import throttle from 'lodash.throttle';
import Button, { LinkButtonType } from '@ferris/react/components/button/Button';
import LiteYouTubeEmbed from 'react-lite-youtube-embed';
import PropTypes from 'prop-types';
import cn from 'classnames';
import parse, { domToReact } from 'html-react-parser';
import { createImpressionEvent, createInteractionEvent } from '../../utils/EventProviders/EgClickstreamUtil';
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css';

function MediaText(props) {
    const {
        headline: defaultHeadline,
        media,
        cta: defaultCta,
        copy: defaultCopy,
        details,
        mediaType,
        arrangement,
        ctaType,
        disclaimerText,
        // AEM props for OKC template - use these if provided, fallback to defaults
        // eslint-disable-next-line camelcase
        testimonial_hero_headline_VR,
        // eslint-disable-next-line camelcase
        testimonial_sub_header_VR,
        // eslint-disable-next-line camelcase
        testimonial_cta_VR
    } = props;

    // Use AEM props if provided, otherwise use defaults
    // eslint-disable-next-line camelcase
    const headline = testimonial_hero_headline_VR || defaultHeadline;
    // eslint-disable-next-line camelcase
    const copy = testimonial_sub_header_VR || defaultCopy;
    // eslint-disable-next-line camelcase
    const cta = testimonial_cta_VR ? { ...defaultCta, text: testimonial_cta_VR } : defaultCta;
    const [isMediaTextSectionViewed, setIsMediaTextSectionViewed] = useState(false);
    const { trigger } = useContext(TrackingContext);
    const MediaTextSection = useRef();

    const isMediaTextSectionVisible = useCallback(() => {
        /* istanbul ignore else */
        if (MediaTextSection.current) {
            return (MediaTextSection.current.getBoundingClientRect().top
            - (MediaTextSection.current.offsetHeight + 150)) < 0;
        }
        return false;
    }, []);

    useEffect(() => {
        const onScroll = () => {
            if (isMediaTextSectionVisible()) {
                setIsMediaTextSectionViewed(true);
            }
        };

        const scrollHandler = throttle(onScroll, 333);

        window.addEventListener('scroll', scrollHandler);
        trigger('impression', createImpressionEvent('image_text_section.presented', 'Property Onboarding', details));

        return () => {
            window.removeEventListener('scroll', scrollHandler);
            scrollHandler.cancel(); // Cancel any pending throttled calls
        };
    }, [trigger, details, isMediaTextSectionVisible]);

    useEffect(() => {
        if (isMediaTextSectionViewed) {
            trigger('impression', createImpressionEvent('image_text_section.viewed', 'Property Onboarding', details));
        }
    }, [isMediaTextSectionViewed, trigger, details]);

    const clickHandler = useCallback(() => {
        trigger('interaction', createInteractionEvent('action_copy_link.selected', 'Property Onboarding', details, `image_text_${details}`));
    }, [trigger, details]);

    const disclaimerParseOptions = {
        replace: ({ attribs, name, children }) => {
            if (!attribs) return;
            if (name === 'a') {
                const isInternalLink = attribs.href && attribs.href.startsWith('#');
                // eslint-disable-next-line consistent-return
                return (
                    <a
                        href={attribs.href}
                        target={isInternalLink ? undefined : '_blank'}
                        rel={isInternalLink ? undefined : 'noreferrer'}
                    >
                        {domToReact(children)}
                    </a>
                );
            }
        }
    };

    return (
        <section
            className={cn(
                'media-text-container',
                { 'media-text-container--left': arrangement === 'left' },
                { 'media-text-container--right': arrangement === 'right' }
            )}
            ref={MediaTextSection}
            id="Testimonials"
            data-testid="media-text"
            data-cta-type={ctaType}
        >
            <div className={cn('media-section', { 'video-player': mediaType === 'video' })}>
                {
                    mediaType === 'image' && (
                        <img
                            alt={media.alt || 'Marketing image showcasing property features'}
                            src={media.url}
                            loading="lazy"
                        />
                    )
                }
                {
                    mediaType === 'video' && (
                        <LiteYouTubeEmbed
                            id={media.url}
                            title="Property testimonial video"
                        />
                    )
                }
            </div>
            <div className={cn('text-section')}>
                <h2>{headline}</h2>
                <div className={cn('media-text-copy')}>{copy ? parse(copy) : ''}</div>
                {
                    cta.text && (
                        <div className={cn('text-cta')}>
                            <a
                                onClick={clickHandler}
                                href={cta.url}
                                target={cta.target}
                                rel="noreferrer"
                                data-has-cta={!!cta.text}
                            >
                                {
                                    ctaType === 'link' && (<span>{cta.text}</span>)
                                }
                                {
                                    ctaType === 'button' && (
                                        <Button
                                            htmlButtonType="button"
                                            type="primary"
                                            onClick={clickHandler}
                                            variant={LinkButtonType.STANDARD}
                                            small
                                        >
                                            {cta.text}
                                        </Button>
                                    )
                                }
                            </a>
                            {disclaimerText && (
                                <div className="text-disclaimer">
                                    {parse(disclaimerText, disclaimerParseOptions)}
                                </div>
                            )}
                        </div>
                    )
                }
            </div>
        </section>
    );
}

const MEDIA_TYPES = ['image', 'video'];
const ARRANGEMENT = ['left', 'right'];
const CTA_TYPES = ['button', 'link'];

MediaText.defaultProps = {
    mediaType: 'image',
    arrangement: 'left',
    ctaType: 'button',
    disclaimerText: undefined,
    // AEM props default to undefined (will use fallback props)
    // eslint-disable-next-line camelcase
    testimonial_hero_headline_VR: undefined,
    // eslint-disable-next-line camelcase
    testimonial_sub_header_VR: undefined,
    // eslint-disable-next-line camelcase
    testimonial_cta_VR: undefined
};

MediaText.propTypes = {
    headline: PropTypes.string.isRequired,
    media: PropTypes.shape({
        url: PropTypes.string.isRequired,
        alt: PropTypes.string
    }).isRequired,
    cta: PropTypes.shape({
        text: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        target: PropTypes.string.isRequired
    }).isRequired,
    copy: PropTypes.string.isRequired,
    details: PropTypes.string.isRequired,
    mediaType: PropTypes.oneOf(MEDIA_TYPES),
    arrangement: PropTypes.oneOf(ARRANGEMENT),
    ctaType: PropTypes.oneOf(CTA_TYPES),
    disclaimerText: PropTypes.string,
    // AEM props for OKC template (optional)
    // eslint-disable-next-line camelcase
    testimonial_hero_headline_VR: PropTypes.string,
    // eslint-disable-next-line camelcase
    testimonial_sub_header_VR: PropTypes.string,
    // eslint-disable-next-line camelcase
    testimonial_cta_VR: PropTypes.string
};

export default MediaText;
